import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import { Video, ArrowRight } from "lucide-react"; 
import { useVideoContext } from "../../context/useVideoContext";
import { useProjectContext } from "../../context/useProjectContext";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useOutletContext } from "react-router-dom";
import { webAppUrl } from "../../constants/OptionsConstants";
import { getAuthBridgeExchangeToken } from "../../api/authBridge";
import LocalStorage from "../../helper/LocalStorage";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import SubscriptionCards from "../SubscriptionsPage/SubscriptionCards";
import Dropdown from "../../Components/Generic/Dropdown";
import Toggle from "../../Components/Generic/Toggle";
import { CURRENCY_OPTIONS, VIDEO_TRANSLATION, getVideoStatusConfig } from "../../constants/OptionsConstants";
import Pill from "../../Components/Generic/Pill";
import { showGenericError } from "../../helper/Notifications";
import usePurchaseSubscription from "../../hooks/usePurchaseSubscription";
import AppDateRangePicker from "../../Components/Generic/AppDateRangePicker";
import PageHeader from "../../Components/Layout/PageHeader";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import MemberAccessBanner from "../../Components/Generic/MemberAccessBanner";
import ProductUsageWidget from "../../Components/Generic/ProductUsageWidget";
import { getDefaultMonthToDateRange } from "../../helper/dateFormat";

const VideoTranslationPage = () => {

  const {
    videosBreakdown, 
    isVideosLoading, 
    totalPages, 
    loadVideoBreakdown
  } = useVideoContext();
  
  const { selectedProject } = useProjectContext();

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { subscription } = useLicensingContext();
  const { setLoading, setShowSupportModal } = useOutletContext();
  const {
    handlePurchase,
    annualPricing,
    setAnnualPricing,
    effectiveAnnual,
    billingInterval,
  } = usePurchaseSubscription();
  const { hasProductAccess: hasMemberVideoAccess } = useMemberProductAccess("video_tool");
  const videoItem = subscription?.items?.find(i => i.product_slug === "video_tool");
  const hasSubscription = !!videoItem || hasMemberVideoAccess;

  const [currency, setCurrency] = useState(() => subscription?.currency || "GBP");

  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);

  useEffect(() => {
    if (!selectedProject?.id) return;
    if (subscription === undefined) return;
    if (!hasSubscription) return;
    
    const [start, end] = dateRange;
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);
    loadVideoBreakdown(page, itemsPerPage, {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
  }, [selectedProject?.id, page, itemsPerPage, dateRange, hasSubscription, subscription]);


  const handleTranslateClick = async () => {
    const projectParam = selectedProject?.id ? `&project_id=${selectedProject.id}` : "";
    const fallbackUrl = `${webAppUrl}/#/?section=tools&tool=video_translation${projectParam}`;
    const currentToken = LocalStorage.getAuthenticationToken();
    if (currentToken) {
      const exchangeToken = await getAuthBridgeExchangeToken(currentToken);
      if (exchangeToken) {
        window.open(`${webAppUrl}/#/login?bridge=${exchangeToken}`, "_blank", "noopener,noreferrer");
        return;
      }
    }
    window.open(fallbackUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (subscription?.currency) {
      setCurrency(subscription.currency);
    }
  }, [subscription]);

  if (subscription === undefined) {
    return <LoaderComponent loading={true} />;
  }

  return (
    <Container>
      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title={hasSubscription ? VIDEO_TRANSLATION : `${VIDEO_TRANSLATION} Plans`}
              subtitle="Translate your video files into multiple languages with natively generated audio."
            />
          </HeaderContent>
          <HeaderRight>
            {!hasSubscription && (
              <Dropdown
                value={currency}
                onValueChange={setCurrency}
                size="default"
                options={CURRENCY_OPTIONS}
                disabled={!!billingInterval}
              />
            )}
            {hasSubscription && (
              <PrimaryButton
                icon={<Video size={20} strokeWidth={2}/>}
                text="Translate Video"
                onClick={handleTranslateClick}
              />
            )}
          </HeaderRight>
        </Header>
        {hasSubscription && <MemberAccessBanner />}
        {hasSubscription && selectedProject && (
          <ProductUsageWidget 
            projectId={selectedProject.id} 
            productSlug="video_tool" 
            dateRange={dateRange}
            calendarAction={
              <AppDateRangePicker 
                value={dateRange} 
                onChange={(newRange) => setDateRange(newRange)} 
              />
            }
          />
        )}

        {!hasSubscription ? (
          <PlanCardsSection>
            <PlanCardsHeader>
              <PricingLabel>Pricing plans</PricingLabel>
              {!billingInterval && (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <Toggle
                    checked={annualPricing}
                    onCheckedChange={setAnnualPricing}
                  />
                  <span className="font-inter text-[16px] leading-6 font-medium text-brand-900">
                    Annual pricing <span className="text-brand-700 font-medium">(save 10%)</span>
                  </span>
                </label>
              )}
            </PlanCardsHeader>

            <SubscriptionCards
              purchaseSubscription={handlePurchase}
              contactSales={() => setShowSupportModal(true)}
              annual={effectiveAnnual}
              currency={currency}
              product="video_tool" 
            />
          </PlanCardsSection>
        ) : (
          <>
          <StyledTable>
            <TableHeader>
              <TableRow>
                <TableHead style={{ textAlign: "left", width: "22%" }}>Name</TableHead>
                <TableHead style={{ textAlign: "center" }}>Status</TableHead>
                <TableHead style={{ textAlign: "center" }}>Minutes</TableHead>
                <TableHead style={{ textAlign: "center" }}>Languages</TableHead>
                <TableHead style={{ textAlign: "center" }}>User</TableHead>
                <TableHead style={{ textAlign: "center" }}>Translated on</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isVideosLoading ? (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: "center", padding: "40px" }}>
                    Loading videos...
                  </TableCell>
                </TableRow>
              ) : videosBreakdown && videosBreakdown.length > 0 ? (
                videosBreakdown.map((video, index) => {
                  const name = video.original_filename || "-";
                  const status = video.status || "-";
                  const minutes = video.duration_seconds ? Math.round(video.duration_seconds / 60) + " min" : "-";
                  const languages = video.source_language && video.target_language ? (
                    <LanguageContainer>
                      <span>{video.source_language}</span>
                      <ArrowRight size={14} strokeWidth={2} color="#667085" />
                      <span>{video.target_language}</span>
                    </LanguageContainer>
                  ) : "-";
                  const date = video.created_at ? new Date(video.created_at).toLocaleDateString() : "-";

                  return (
                    <TableRow key={video.job_id || index}>
                      <TableCell>
                        <NameContainer>
                          <TableIconWrapper>
                            <Video size={20} strokeWidth={2} />
                          </TableIconWrapper>
                          <VideoNameText>{name}</VideoNameText>
                        </NameContainer>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        {status === "-" ? (
                          "-"
                        ) : (
                          <Pill variant={getVideoStatusConfig(status).variant} size="sm">
                            {getVideoStatusConfig(status).label}
                          </Pill>
                        )}
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        {minutes}
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        {languages}
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <TruncatedText title={video.user_label || undefined}>
                          {video.user_label || "—"}
                        </TruncatedText>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        {date}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    style={{ textAlign: "center", padding: "40px 20px" }}
                  >
                    <NoDataText>
                      No videos translated yet. Click "Translate video" to get started.
                    </NoDataText>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            
            <TablePagination
              page={page}
              totalPages={totalPages}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
              leftColSpan={2}
              centerColSpan={2}
              rightColSpan={2}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
            />

          </StyledTable>
          </>
        )}

      </PageContent>
    </Container>
  );
};

export default VideoTranslationPage;

// STYLED COMPONENTS

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 30px 30px 18px 30px;
  position: relative;
  height: 100%;
  background: #f9fafb;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: white;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const VideoNameText = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 500;
`;

const NoDataText = styled.div`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TableIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: #F4EBFF; 
  border-radius: 50%;        
  color: #7F56D9;            
`;

const LanguageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const TruncatedText = styled.span`
  display: block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: #344054;
`;

const PlanCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const PlanCardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
`;

const PricingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 2px 12px;
  border-radius: 16px;
  background: #F4EBFF;
  color: #6941C6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;